import React, { useCallback } from 'react'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import ModalForm from 'core/elements/modal/ModalForm'
import useReactRouter from 'use-react-router'
import useParams from 'core/hooks/useParams'
import { routes } from 'core/utils/routes'
import Text from 'core/elements/Text'
import RadioSelectableCard from 'k8s/components/common/RadioSelectableCard'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { createSecret } from './actions'
import DropdownField from 'core/components/validatedForm/DropdownField'
import ClusterPicklist from 'k8s/components/common/ClusterPicklist'
import SecretYamlParamFields from './SecretYamlParamFields'
import SecretParamFields from './SecretParamFields'
import jsYaml from 'js-yaml'
import usePluginRouter from 'core/hooks/usePluginRouter'

export default function AddSecretModal({ addRoute }) {
  const { history } = useReactRouter()
  const classes = useStyles()
  const { currentPluginId } = usePluginRouter()
  const routePath = currentPluginId === 'kubevirt' ? 'kubevirtConfiguration' : 'configuration'

  const defaultParams = {
    method: 'form',
    clusterId: null,
    name: '',
    namespace: null,
    type: null,
    immutable: undefined,
    data: {},
    stringData: {},
    yaml: '',
  }

  const { params, updateParams, setParams } = useParams<{
    method: string
    clusterId: string
    name: string
    namespace: string
    type?: string
    immutable?: boolean
    data?: any
    stringData?: any
    yaml?: string
  }>(defaultParams)

  const { update, updating, error, reset } = useUpdateAction(createSecret)

  const handleClose = () => {
    setParams(defaultParams)
    reset()
    history.push(routes[routePath].secrets.list.path())
  }

  const submitForm = useCallback(async () => {
    if (params?.method === 'yaml') {
      const body = jsYaml.load(params?.yaml)
      const { success } = await update({
        clusterId: params?.clusterId,
        namespace: params?.namespace || body?.metadata?.namespace,
        body,
      })
      if (success) handleClose()
    } else {
      const body = {
        apiVersion: 'v1',
        kind: 'Secret',
        metadata: {
          name: params?.name,
        },
        type: params?.type,
        data: params?.data,
        stringData: params?.stringData,
        immutable: params?.immutable,
      }

      const { success } = await update({
        clusterId: params?.clusterId,
        namespace: params?.namespace,
        body,
      })
      if (success) handleClose()
    }
  }, [params, handleClose])

  return (
    <ModalForm
      route={addRoute}
      title="Add Secret"
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={updating}
      error={error}
      maxWidth={528}
    >
      <div className={classes.container}>
        <Text variant="body2">
          To start configuration choose the cluster and the method of creation.
        </Text>
        <DropdownField
          DropdownComponent={ClusterPicklist}
          id="clusterId"
          label="Select Cluster"
          value={params?.clusterId}
          onChange={(value) => updateParams({ clusterId: value })}
          showAll={false}
          required
        />
        <div className={classes.radioCards}>
          <Text variant="caption2" className={classes.radioText}>
            Select one of the below methods
          </Text>
          <RadioSelectableCard
            onClick={() => updateParams({ method: 'form' })}
            label="Fill in the form"
            active={params?.method === 'form'}
            subtitle=""
          />
          <RadioSelectableCard
            onClick={() => updateParams({ method: 'yaml' })}
            label="Upload or paste YAML"
            active={params?.method === 'yaml'}
            subtitle=""
          />
        </div>
        <hr className={classes.divider} />
        {params?.method === 'yaml' && (
          <SecretYamlParamFields params={params} updateParams={updateParams} />
        )}
        {params?.method === 'form' && (
          <div className={classes.fields}>
            <SecretParamFields params={params} updateParams={updateParams} />
          </div>
        )}
      </div>
    </ModalForm>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  container: {
    padding: '16px 40px 40px 32px',
    display: 'grid',
    gap: 32,
  },
  radioText: {
    textTransform: 'uppercase',
    color: theme.components.typography.passive,
  },
  radioCards: {
    display: 'grid',
    gap: 16,
  },
  divider: {
    height: 1,
    background: theme.components.card.border,
    border: 0,
    width: '100%',
  },
  fields: {
    display: 'grid',
    gap: 24,
  },
}))
